import { Box, Typography, Chip, Tooltip, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EmojiEvents, BarChart } from '@mui/icons-material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { thSx, tdSx, getStyle, getTier, getFailLabel, getFailList } from './constants';
import { Card, CardTitle, ScoreBar, BreakoutBarBadge, RiskBar, ComponentLabel } from './primitives';
import StyleChip from './StyleChip';
import AIConfidenceBadge from '../../components/AIConfidenceBadge';

const LeadersSection = ({
  leaders, openHistory, exportingImage, handleExportLeadersImage,
  leadersExportRef, styleFilter, setStyleFilter, sn, t, actionBoard,
}) => {
  if (!leaders || (leaders.leaders.length === 0 && !(leaders.emerging?.length > 0))) return null;

  const allRows = [...(leaders.leaders || []), ...(leaders.emerging || [])];
  const styleCounts = allRows.reduce((acc, r) => {
    const k = r.trade_style || 'NONE';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const orderedStyles = [
    'EARLY_BREAKOUT', 'WATCHLIST', 'LONG_TREND_HOLD', 'SWING_TREND_SETUP',
    'TPLUS_MOMENTUM', 'OVEREXTENDED_RISK', 'TREND_WEAKENING', 'BROKEN_TREND',
  ];
  const chips = [
    { key: 'ALL', label: t('stockStrength.filter.all'), Icon: null, count: allRows.length, color: '#475569' },
    ...orderedStyles
      .filter(k => styleCounts[k] > 0)
      .map(k => {
        const m = getStyle(k);
        return { key: k, label: m.label, Icon: m.Icon, count: styleCounts[k], color: m.color };
      }),
  ];

  return (
    <Box ref={leadersExportRef} sx={{ mb: 3 }}>
      <Card>
        <CardTitle
          icon={EmojiEvents}
          action={(
            <Button
              variant="outlined"
              size="small"
              startIcon={exportingImage ? <CircularProgress size={14} color="inherit" /> : <DownloadRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={handleExportLeadersImage}
              disabled={exportingImage}
              data-html2canvas-ignore="true"
              sx={{
                minWidth: 0, height: 24, px: 1, borderRadius: 1,
                textTransform: 'none', fontSize: '0.68rem', fontWeight: 700,
                borderColor: '#cbd5e1', color: '#475569', bgcolor: '#fff',
                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
              }}
            >
              {exportingImage
                ? t('stockStrength.actions.exportingImage', 'Đang xuất...')
                : t('stockStrength.actions.exportImage', 'Xuất ảnh')}
            </Button>
          )}
        >
          {t('stockStrength.sections.leadersEmerging', { leaders: leaders.leader_count, emerging: leaders.emerging_count || 0 })}
          <Typography component="span" sx={{ fontSize: '0.65rem', color: '#94a3b8', ml: 1 }}>
            {t('stockStrength.sections.leadersDesc')}
          </Typography>
        </CardTitle>

        {/* Trade-style filter chips */}
        <Box sx={{ px: 1.5, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.6, borderBottom: '1px dashed #e2e8f0', bgcolor: '#fafbfc' }}>
          {chips.map(c => {
            const active = styleFilter === c.key;
            return (
              <Chip
                key={c.key}
                icon={c.Icon ? <c.Icon sx={{ fontSize: 13, color: active ? '#fff !important' : `${c.color} !important` }} /> : undefined}
                label={`${c.label} (${c.count})`}
                size="small"
                onClick={() => setStyleFilter(c.key)}
                sx={{
                  height: 24, fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer',
                  bgcolor: active ? c.color : alpha(c.color, 0.08),
                  color: active ? '#fff' : c.color,
                  border: `1px solid ${alpha(c.color, active ? 1 : 0.3)}`,
                  '&:hover': { bgcolor: active ? c.color : alpha(c.color, 0.18) },
                  '& .MuiChip-label': { px: 0.9 },
                }}
              />
            );
          })}
        </Box>

        {/* Leaders + Emerging table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>#</TableCell>
                <TableCell sx={thSx}>{t('stockStrength.table.ticker')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.score')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.aiConf', 'AI')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.style')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.rs')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.trend')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.mom')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.flow')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.resil')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.risk')}</TableCell>
                <TableCell sx={thSx} align="center">{t('stockStrength.table.tier')}</TableCell>
                <TableCell sx={thSx}>{t('stockStrength.table.missing')}</TableCell>
                <TableCell sx={thSx}>{t('stockStrength.table.sector')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ...leaders.leaders.map(l => ({ ...l, _tier: 'LEADER', criteria_fails: '' })),
                ...(leaders.emerging || []).map(e => ({ ...e, _tier: 'EMERGING' })),
              ].filter(s => styleFilter === 'ALL' || (s.trade_style || 'NONE') === styleFilter).map((s, i) => {
                const tier = getTier(s._tier);
                const fails = getFailList(s);
                return (
                  <TableRow
                    key={s.ticker}
                    hover
                    onClick={() => openHistory(s.ticker)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: s._tier === 'LEADER' ? alpha('#10b981', 0.04) : alpha('#3b82f6', 0.03),
                      '&:hover': { bgcolor: s._tier === 'LEADER' ? alpha('#10b981', 0.09) : alpha('#3b82f6', 0.08) },
                      transition: 'all 0.15s ease',
                      borderLeft: `3px solid ${s._tier === 'LEADER' ? '#10b981' : '#3b82f6'}`,
                    }}
                  >
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>{i + 1}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: tier.color }}>
                          {s.ticker}
                        </Typography>
                        {s.breakout_bar_flag && <BreakoutBarBadge t={t} />}
                      </Box>
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <ScoreBar value={s.strength_score} color={tier.color} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <AIConfidenceBadge
                        confidence={s.ai_confidence}
                        tooltipTitle={t(
                          'stockStrength.table.aiConfTooltip',
                          'AI confidence \u2014 pattern reliability win probability',
                        )}
                        compact
                      />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <StyleChip style={s.trade_style} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <ComponentLabel levelKey={s.level_keys?.rs}    value={s.components.relative_strength} tFn={t} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <ComponentLabel levelKey={s.level_keys?.trend} value={s.components.trend_quality} tFn={t} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <ComponentLabel levelKey={s.level_keys?.mom}   value={s.components.momentum_quality} tFn={t} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <ComponentLabel levelKey={s.level_keys?.flow}  value={s.components.money_flow} tFn={t} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <ComponentLabel levelKey={s.level_keys?.resil} value={s.components.resilience} tFn={t} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <RiskBar levelKey={s.level_keys?.risk} value={s.components.risk_penalty} t={t} />
                    </TableCell>
                    <TableCell sx={tdSx} align="center">
                      <Chip label={`${tier.icon} ${t('stockStrength.tiers.' + tier.labelKey)}`} size="small" sx={{
                        height: 20, fontSize: '0.6rem', fontWeight: 700,
                        bgcolor: alpha(tier.color, 0.08), color: tier.color,
                        border: `1px solid ${alpha(tier.color, 0.2)}`,
                      }} />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      {fails.length > 0 && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#f59e0b', fontWeight: 500 }}>
                          {fails.map(f => getFailLabel(f, t)).join(', ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={s.sector || ''}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          {sn(s.sector)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Persistent Leaders sub-table */}
        {leaders.persistent_leaders && leaders.persistent_leaders.length > 0 && (
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, mb: 1, px: 1.5 }}>
              {t('stockStrength.sections.persistentLeaders', { count: leaders.persistent_leader_count })}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>#</TableCell>
                    <TableCell sx={thSx}>{t('stockStrength.table.ticker')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.score')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.aiConf', 'AI')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.rs')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.trend')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.mom')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.flow')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.resil')}</TableCell>
                    <TableCell sx={thSx} align="center">{t('stockStrength.table.risk')}</TableCell>
                    <TableCell sx={thSx}>{t('stockStrength.table.sector')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaders.persistent_leaders.map((p, i) => (
                    <TableRow
                      key={p.ticker}
                      hover
                      onClick={() => openHistory(p.ticker)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: alpha('#8b5cf6', 0.03),
                        '&:hover': { bgcolor: alpha('#8b5cf6', 0.07) },
                        transition: 'all 0.15s ease',
                        borderLeft: '3px solid #8b5cf6',
                      }}
                    >
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>{p.strength_rank}</Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#8b5cf6' }}>{p.ticker}</Typography>
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <ScoreBar value={p.strength_score} color="#8b5cf6" />
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <AIConfidenceBadge
                          confidence={p.ai_confidence}
                          tooltipTitle={t(
                            'stockStrength.table.aiConfTooltip',
                            'AI confidence \u2014 pattern reliability win probability',
                          )}
                          compact
                        />
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <ComponentLabel levelKey={p.level_keys?.rs}    value={p.components?.relative_strength} tFn={t} />
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <ComponentLabel levelKey={p.level_keys?.trend} value={p.components?.trend_quality} tFn={t} />
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <ComponentLabel levelKey={p.level_keys?.mom}   value={p.components?.momentum_quality} tFn={t} />
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <ComponentLabel levelKey={p.level_keys?.flow}  value={p.components?.money_flow} tFn={t} />
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <ComponentLabel levelKey={p.level_keys?.resil} value={p.components?.resilience} tFn={t} />
                      </TableCell>
                      <TableCell sx={tdSx} align="center">
                        <RiskBar levelKey={p.level_keys?.risk} value={p.components?.risk_penalty} t={t} />
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Tooltip title={p.sector || ''}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{sn(p.sector)}</Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Card>

      {/* Backtest provenance footer */}
      {actionBoard?.provenance && (
        <Box sx={{
          mt: 1, px: 1.5, py: 1,
          bgcolor: '#fafbfc', border: '1px dashed #e2e8f0', borderRadius: 1.5,
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <BarChart sx={{ fontSize: 16, color: '#64748b' }} />
            <Typography component="span" sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>
              <b>{t('stockStrength.provenance.backtestLabel')}:</b> {actionBoard.provenance.snapshots} {t('stockStrength.provenance.weeklySessions')} ·
              {' '}{actionBoard.provenance.total_signals.toLocaleString()} {t('stockStrength.provenance.signals')} ·
              {' '}{actionBoard.provenance.tickers} {t('stockStrength.provenance.tickersUnit')} · {actionBoard.provenance.period}
            </Typography>
          </Box>
          <Typography component="span" sx={{ fontSize: '0.62rem', color: '#94a3b8', fontStyle: 'italic' }}>
            {t('stockStrength.provenance.buyLabel')}: {t('stockStrength.provenance.wr40d')} <b style={{ color: '#10b981' }}>{actionBoard.provenance.buy_now_winrate_40d}%</b> ·
            {' '}{t('stockStrength.provenance.avg')} <b style={{ color: '#10b981' }}>+{actionBoard.provenance.buy_now_avg_return_40d}%</b> ·
            {' '}{t('stockStrength.provenance.pf')} <b style={{ color: '#10b981' }}>{actionBoard.provenance.buy_now_profit_factor_40d}</b> ·
            {' '}{t('stockStrength.provenance.alpha')} <b style={{ color: '#10b981' }}>+{actionBoard.provenance.alpha_pp}pp</b> {t('stockStrength.provenance.vsBaseline')}
            {' '}{t('stockStrength.provenance.disclaimer')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LeadersSection;
